import { Avatar, AvatarFallback } from '@/ui/components/avatar';
import { cn } from '@/ui/utils';
import { CSSProperties } from 'react';

interface Props extends React.ComponentProps<typeof Avatar> {
  user: {
    id: string;
    name?: string | undefined;
    email?: string | undefined;
    image?: string | undefined;
  };
  pending?: boolean;
  size?: number;
  // Explicit background override (concrete CSS color). When set, takes
  // precedence over the hashed palette — used to match collab cursor colors.
  color?: string;
}

const AVATAR_COLORS = [
  'bg-indigo-600',
  'bg-violet-600',
  'bg-pink-600',
  'bg-rose-600',
  'bg-amber-600',
  'bg-emerald-600',
  'bg-teal-600',
  'bg-cyan-600',
  'bg-blue-600',
  'bg-purple-600',
];

// Concrete hex equivalents of AVATAR_COLORS, same order — lets non-DOM
// consumers (e.g. collab cursors) derive the exact same per-user color.
const AVATAR_COLOR_HEX = [
  '#4f46e5',
  '#7c3aed',
  '#db2777',
  '#e11d48',
  '#d97706',
  '#059669',
  '#0d9488',
  '#0891b2',
  '#2563eb',
  '#9333ea',
];

const hashIndex = (key: string, length: number): number => {
  return key.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % length;
};

const getColorFromString = (string: string): string => {
  if (string === '') return 'bg-surface-9';
  return AVATAR_COLORS[hashIndex(string, AVATAR_COLORS.length)] ?? 'bg-surface-9';
};

// Per-user color as a concrete hex value, matching the avatar's hashed palette.
export const getUserColorHex = (key: string): string => {
  if (!key) return '#71717a';
  return AVATAR_COLOR_HEX[hashIndex(key, AVATAR_COLOR_HEX.length)] ?? '#71717a';
};

export function UserAvatar({
  className,
  pending = false,
  user,
  size = 24,
  color,
  ...props
}: Props) {
  const firstName = user.name?.split(' ')[0];
  const lastName = user.name?.split(' ')[1];
  const bgColor = getColorFromString(user.email ?? user.id ?? '');

  return (
    <Avatar
      style={{ '--avatar-size': `${size}px` } as CSSProperties}
      className={cn('size-(--avatar-size)! shrink-0', className)}
      {...props}
    >
      <AvatarFallback
        style={{
          fontSize: 'calc(var(--avatar-size) * 0.45)',
          ...(color && !pending ? { backgroundColor: color } : {}),
        }}
        className={cn(
          'text-white',
          pending
            ? 'border-dashed border border-surface-7 bg-white text-muted'
            : color
              ? ''
              : bgColor,
        )}
      >
        {user.name?.trim() === '' ? (
          user.email?.[0]
        ) : (
          <>
            {firstName?.[0]}
            {lastName?.[0]}
          </>
        )}
      </AvatarFallback>
    </Avatar>
  );
}
